import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router';

import { parseDateFromYYMMDD } from '../components/materials/datepickers/datepicker-in-excel';
import { getDefaultPlanData } from '../hooks/use-excel-table';
import { defaultPlanValue } from '../stores/create-plan-table.store';

import type { CreatePlanRequest } from '../types/plan.types';

import { CustomDatePicker } from '@/components/search';
import { PLAN_FIELDS, TYPE_BOOLEAN } from '@/lib/constants';

/**
 * Plan Calendar Section Component
 * Displays a calendar with event dates for a specific plan
 */
type PlanCalendarSectionProps = {
  data: CreatePlanRequest[]; // filteredPlanDeliveries - used for past dates dots (only when filter allows)
  tableData?: (string | number)[][]; // filteredTableData - visible records for tracking edited dates
  fullTableData?: (string | number)[][]; // fullTableData - ALL records for immediate calendar update when user edits
  visibleRowIndices?: Set<number>; // Indices of rows that are currently visible (to track edited dates)
  editedRowIndices?: Set<number>; // Indices of rows that have been edited by user (to show dots for edited dates)
  oldRecordIndices?: Set<number>; // Indices of rows that are "old records" (past dates from initial load)
  filters?: {
    isShowPast: boolean;
    isShowArchive: boolean;
  };
};

/**
 * Normalize a date string (e.g. ISO from API) to YYYY-MM-DD for consistent comparison with tableDataDates/countMap
 */
const normalizePlacementDateString = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Extract and normalize placementDate from a table row
 */
const getPlacementDateFromRow = (
  row: (string | number)[],
  placementDateIndex: number
): string | null => {
  if (row.length <= placementDateIndex) return null;
  const placementDate = row[placementDateIndex];
  if (!placementDate) return null;
  const dateStr = String(placementDate);
  return parseDateFromYYMMDD(dateStr) || dateStr;
};

/**
 * Check if a row is deleted
 */
const isRowDeleted = (row: (string | number)[], isDeletedIndex: number): boolean => {
  return row.length > isDeletedIndex && row[isDeletedIndex] === TYPE_BOOLEAN.TRUE;
};

/**
 * Check if a row is archived
 */
const isRowArchived = (row: (string | number)[], isArchiveIndex: number): boolean => {
  return (
    row.length > isArchiveIndex &&
    (row[isArchiveIndex] === TYPE_BOOLEAN.TRUE || row[isArchiveIndex] === 1)
  );
};

const DEFAULT_PLAN_ID_COLUMN_INDEX = PLAN_FIELDS.PLAN_ID.id - 1;
const DEFAULT_PLACEMENT_DATE_COLUMN_INDEX = PLAN_FIELDS.PLACEMENT_DATE.id - 1;
const DEFAULT_UNTOUCHED_DETAIL_ROW = getDefaultPlanData(0, defaultPlanValue, [], [], true, true);

const normalizeCellValue = (value: string | number | undefined): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const isUntouchedBlankRow = (row: (string | number)[]): boolean => {
  const id = row[DEFAULT_PLAN_ID_COLUMN_INDEX];
  if (id && String(id).trim() !== '') {
    return false;
  }

  return DEFAULT_UNTOUCHED_DETAIL_ROW.every((defaultValue, index) => {
    if (index === DEFAULT_PLACEMENT_DATE_COLUMN_INDEX) {
      return true;
    }
    if (index === DEFAULT_PLAN_ID_COLUMN_INDEX) {
      return normalizeCellValue(row[index]) === '';
    }
    return normalizeCellValue(row[index]) === normalizeCellValue(defaultValue as string | number);
  });
};

/**
 * Count dates from data prop (original API data)
 * IMPORTANT: Also check isArchive filter - only count archived records if filter allows
 */
const countDatesFromData = (
  data: CreatePlanRequest[],
  countMap: Record<string, number>,
  filters?: { isShowArchive: boolean }
): void => {
  data?.forEach((plan) => {
    if (plan.placementDate) {
      // Check if plan is archived - only count if filter allows
      if (plan.isArchive && !filters?.isShowArchive) {
        // Skip archived records if filter is not enabled
        return;
      }
      countMap[plan.placementDate] = (countMap[plan.placementDate] ?? 0) + 1;
    }
  });
};

/**
 * Find row in tableData that matches the given planId
 */
const findRowByPlanId = (
  tableData: (string | number)[][],
  planId: string
): (string | number)[] | undefined => {
  const PLAN_ID_COLUMN_INDEX = PLAN_FIELDS.PLAN_ID.id - 1;
  const IS_DELETED_COLUMN_INDEX = PLAN_FIELDS.IS_DELETED.id - 1;

  return tableData.find((row) => {
    if (isRowDeleted(row, IS_DELETED_COLUMN_INDEX)) {
      return false;
    }
    if (row.length > PLAN_ID_COLUMN_INDEX) {
      return row[PLAN_ID_COLUMN_INDEX] === planId;
    }
    return false;
  });
};

/**
 * Get normalized placementDate from a matching row
 */
const getPlacementDateFromMatchingRow = (
  matchingRow: (string | number)[] | undefined
): string | null => {
  if (!matchingRow) return null;
  const PLACEMENT_DATE_COLUMN_INDEX = PLAN_FIELDS.PLACEMENT_DATE.id - 1;
  return getPlacementDateFromRow(matchingRow, PLACEMENT_DATE_COLUMN_INDEX);
};

export const PlanCalendarSection: React.FC<PlanCalendarSectionProps> = ({
  data,
  tableData,
  fullTableData,
  visibleRowIndices,
  editedRowIndices,
  oldRecordIndices,
  filters,
}) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [searchParams] = useSearchParams();
  const selectedPlanId = searchParams.get('selectedPlanId');

  // Create serialized key from fullTableData placementDate values to force CustomDatePicker re-render
  // IMPORTANT: Use fullTableData (all records) instead of filteredTableData to ensure calendar updates
  // immediately when user edits dates, regardless of filter state
  const placementDatesKey = useMemo(() => {
    const dataToUse = fullTableData || tableData;
    if (!dataToUse || dataToUse.length === 0) return '';
    const PLACEMENT_DATE_COLUMN_INDEX = PLAN_FIELDS.PLACEMENT_DATE.id - 1;
    const IS_DELETED_COLUMN_INDEX = PLAN_FIELDS.IS_DELETED.id - 1;
    return dataToUse
      .map((row, index) => {
        if (isRowDeleted(row, IS_DELETED_COLUMN_INDEX)) {
          return '';
        }
        const normalizedDate = getPlacementDateFromRow(row, PLACEMENT_DATE_COLUMN_INDEX);
        return normalizedDate ? `${index}:${normalizedDate}` : `${index}:`;
      })
      .filter((item) => item !== '')
      .join('|');
  }, [fullTableData, tableData]);

  const { dateCountMap, eventDates, initialDate, minDate, maxDate } = useMemo(() => {
    const countMap: Record<string, number> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // IMPORTANT: Use fullTableData (all records) for immediate calendar update when user edits
    // This ensures calendar reflects changes immediately, regardless of filter state
    // If fullTableData is not provided, fallback to tableData (filteredTableData)
    const dataToCount = fullTableData || tableData;
    const tableDataDates = new Set<string>();

    // IMPORTANT: Separate logic for past dates and current/future dates
    // 1. Count dates from fullTableData (all records, not filtered)
    //    IMPORTANT:
    //    - "Current/future records": ALWAYS show dots (regardless of filter)
    //    - "Old records" (past dates from initial load): only show if filter allows
    //    - Edited records (current/future records edited to past): ALWAYS show dots
    //    This ensures calendar shows dots correctly:
    //    - On initial load: only current/future dates (if filter not enabled for old records)
    //    - When user edits: show dots for edited dates even if past (because it's a current/future record)
    //    Example: 3 records at date 25, user edits 1 to date 22
    //    -> Calendar shows 2 dots at date 25, 1 dot at date 22 (immediately, because it's a current/future record)
    if (dataToCount && dataToCount.length > 0) {
      const PLACEMENT_DATE_COLUMN_INDEX = PLAN_FIELDS.PLACEMENT_DATE.id - 1;
      const IS_DELETED_COLUMN_INDEX = PLAN_FIELDS.IS_DELETED.id - 1;
      const IS_ARCHIVE_COLUMN_INDEX = PLAN_FIELDS.IS_ARCHIVE.id - 1;

      dataToCount.forEach((row, index) => {
        if (isRowDeleted(row, IS_DELETED_COLUMN_INDEX)) {
          return;
        }

        // Skip untouched blank rows appended for edit UX
        if (isUntouchedBlankRow(row)) {
          return;
        }

        // Check if row is archived - only count if filter allows
        const isArchived = isRowArchived(row, IS_ARCHIVE_COLUMN_INDEX);
        if (isArchived && !filters?.isShowArchive) {
          // Skip archived records if filter is not enabled
          return;
        }

        const normalizedDate = getPlacementDateFromRow(row, PLACEMENT_DATE_COLUMN_INDEX);
        if (normalizedDate) {
          const rowDate = new Date(normalizedDate);
          rowDate.setHours(0, 0, 0, 0);

          tableDataDates.add(normalizedDate);

          // IMPORTANT: Check if this is an "old record" (past date from initial load)
          const isOldRecord = oldRecordIndices ? oldRecordIndices.has(index) : false;

          if (isOldRecord) {
            // This is an "old record" - only show dots if filter allows
            const isPastDate = rowDate < today;
            if (!isPastDate || filters?.isShowPast) {
              // Show dots for old records if filter allows
              countMap[normalizedDate] = (countMap[normalizedDate] ?? 0) + 1;
            }
          } else {
            // This is a "current/future record" - ALWAYS show dots (regardless of filter)
            // This includes:
            // - Records that were future/current at initial load
            // - Records that were edited (even if edited to past date)
            // - New rows added by user
            countMap[normalizedDate] = (countMap[normalizedDate] ?? 0) + 1;
          }
        }
      });
    }

    // 2. Past dates from API: only when there is NO table data (dataToCount empty).
    //    When we have table data, the table is the single source of truth for dots; we must not add
    //    dates from data, otherwise a deleted row's date (still in API until save) would keep showing.
    if (
      (!dataToCount || dataToCount.length === 0) &&
      filters?.isShowPast &&
      data &&
      data.length > 0
    ) {
      data.forEach((plan) => {
        if (plan.placementDate) {
          if (plan.isArchive && !filters?.isShowArchive) return;
          const normalizedPlanDate = normalizePlacementDateString(plan.placementDate);
          const planDate = new Date(plan.placementDate);
          planDate.setHours(0, 0, 0, 0);
          if (planDate < today && !tableDataDates.has(normalizedPlanDate)) {
            countMap[normalizedPlanDate] = (countMap[normalizedPlanDate] ?? 0) + 1;
          }
        }
      });
    }

    // Fallback: If no fullTableData/tableData, use data prop for all dates
    if ((!dataToCount || dataToCount.length === 0) && data && data.length > 0) {
      countDatesFromData(data, countMap, filters);
    }

    // Cleanup: Remove dots for dates that exist in API (data) but NOT in current table (dataToCount).
    // This fixes the case where user deletes a row locally: the date is still in filteredPlanDeliveries
    // until save, but must not show a dot because the row was removed from the table.
    // IMPORTANT: Normalize plan.placementDate to YYYY-MM-DD so it matches tableDataDates/countMap keys
    // (API may return ISO e.g. "2026-01-08T00:00:00.000Z" while table uses "2026-01-08").
    if (dataToCount && dataToCount.length > 0 && data && data.length > 0) {
      data.forEach((plan) => {
        if (plan.placementDate) {
          const normalizedPlanDate = normalizePlacementDateString(plan.placementDate);
          if (!tableDataDates.has(normalizedPlanDate)) {
            const planDate = new Date(plan.placementDate);
            planDate.setHours(0, 0, 0, 0);
            if (planDate >= today) {
              delete countMap[normalizedPlanDate];
            }
          }
        }
      });
    }

    const dates = Object.keys(countMap);

    // Calculate min and max dates from eventDates
    let min: string | undefined;
    let max: string | undefined;
    if (dates.length > 0) {
      const sortedDates = [...dates].sort((a, b) => a.localeCompare(b));
      min = sortedDates[0];
      max = sortedDates[sortedDates.length - 1];
    }

    // Find the month closest to current month (absolute distance, no preference)
    // Returns current date if no filtered data available
    const findClosestMonth = (): string => {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth(); // 0-11

      if (dates.length === 0) {
        // Return current date if no filtered data
        const month = String(currentMonth + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${currentYear}-${month}-${day}`;
      }

      // Get unique months from dates (year-month combination)
      const monthSet = new Set<string>();
      const monthToDateMap = new Map<string, string>();

      for (const dateStr of dates) {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = date.getMonth();
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

        monthSet.add(monthKey);
        // Store first date found for this month
        if (!monthToDateMap.has(monthKey)) {
          monthToDateMap.set(monthKey, dateStr);
        }
      }

      // Find the month with minimum absolute distance from current month
      let closestMonth: string | undefined;
      let minDiff = Infinity;

      for (const monthKey of monthSet) {
        const [yearStr, monthStr] = monthKey.split('-');
        const year = Number.parseInt(yearStr, 10);
        const month = Number.parseInt(monthStr, 10) - 1; // Convert back to 0-11

        // Calculate absolute difference in months
        const diff = Math.abs((year - currentYear) * 12 + (month - currentMonth));

        if (diff < minDiff) {
          minDiff = diff;
          closestMonth = monthKey;
        }
      }

      // Return closest month date, or current date as fallback (should not happen if dates.length > 0)
      const closestDate = closestMonth ? monthToDateMap.get(closestMonth) : undefined;
      return closestDate ?? `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
    };

    /**
     * Extract visible dates from tableData (filtered data - only visible records)
     */
    const extractVisibleDatesFromTableData = (): string[] => {
      const visibleDates: string[] = [];

      if (!tableData || tableData.length === 0) {
        return visibleDates;
      }

      const PLACEMENT_DATE_COLUMN_INDEX = PLAN_FIELDS.PLACEMENT_DATE.id - 1;
      const IS_DELETED_COLUMN_INDEX = PLAN_FIELDS.IS_DELETED.id - 1;

      tableData.forEach((row) => {
        if (isRowDeleted(row, IS_DELETED_COLUMN_INDEX)) {
          return;
        }

        if (isUntouchedBlankRow(row)) {
          return;
        }

        const normalizedDate = getPlacementDateFromRow(row, PLACEMENT_DATE_COLUMN_INDEX);
        if (normalizedDate) {
          visibleDates.push(normalizedDate);
        }
      });

      return visibleDates;
    };

    /**
     * Handle case when there are no visible records
     * Check if selectedPlanId exists for initial page load highlight
     */
    const handleNoVisibleRecords = (): string => {
      if (!selectedPlanId) {
        return findClosestMonth();
      }

      // Check in fullTableData for initial highlight
      if (fullTableData && fullTableData.length > 0) {
        const matchingRow = findRowByPlanId(fullTableData, selectedPlanId);
        const normalizedDate = getPlacementDateFromMatchingRow(matchingRow);
        if (normalizedDate) {
          return normalizedDate;
        }
      }

      // Check in data prop (filteredPlanDeliveries)
      const noticeDelivery = data?.find((delivery) => delivery.id === selectedPlanId);
      if (noticeDelivery?.placementDate) {
        return noticeDelivery.placementDate;
      }

      // No visible records and no selectedPlanId, fallback to closest month
      return findClosestMonth();
    };

    /**
     * Get selectedPlanId date from tableData if it exists and is in visibleDates
     */
    const getSelectedPlanIdDateFromTableData = (visibleDates: string[]): string | null => {
      if (!selectedPlanId || !tableData || tableData.length === 0) {
        return null;
      }

      const matchingRow = findRowByPlanId(tableData, selectedPlanId);
      if (!matchingRow) {
        return null;
      }

      const normalizedDate = getPlacementDateFromMatchingRow(matchingRow);
      if (normalizedDate && visibleDates.includes(normalizedDate)) {
        return normalizedDate;
      }

      return null;
    };

    /**
     * Get today's date string in YYYY-MM-DD format
     */
    const getTodayString = (): string => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    };

    /**
     * Calculate initial date when isShowPast = true
     * Priority: use oldest record
     */
    const calculateInitialDateWithIsShowPastTrue = (sortedDates: string[]): string => {
      return sortedDates[0];
    };

    /**
     * Calculate initial date when isShowPast = false
     * Priority: today's date (if exists), else closest date to today (past or future)
     */
    const calculateInitialDateWithIsShowPastFalse = (
      sortedDates: string[],
      todayStr: string,
      selectedPlanIdDate: string | null
    ): string => {
      // Check if today's date exists in visible records
      const todayIndex = sortedDates.findIndex((date) => date === todayStr);
      if (todayIndex !== -1) {
        // Today's date exists, use it
        // Only use selectedPlanId date if it matches today (for highlighting)
        if (selectedPlanIdDate === todayStr) {
          return todayStr;
        }

        return todayStr;
      }

      // Today's date doesn't exist, find closest date to today (past or future)
      const todayDate = new Date(todayStr);
      let closestDate: string | null = null;
      let minDiff = Infinity;

      for (const dateStr of sortedDates) {
        const date = new Date(dateStr);
        const diff = Math.abs(date.getTime() - todayDate.getTime());

        if (diff < minDiff) {
          minDiff = diff;
          closestDate = dateStr;
        }
      }

      // Use closest date if found
      if (closestDate) {
        // Only use selectedPlanId date if it matches closest date (for highlighting)
        if (selectedPlanIdDate === closestDate) {
          return closestDate;
        }

        return closestDate;
      }

      // Fallback: use newest record (shouldn't happen if sortedDates.length > 0)
      return sortedDates[sortedDates.length - 1];
    };

    /**
     * Calculate initial date based on currently displayed records in tableData
     * Priority:
     * 1. If selectedPlanId exists and record is STILL VISIBLE in tableData -> use its date (for highlighting)
     * 2. If isShowPast = false: prioritize today's date (if exists), else closest date to today (past or future)
     * 3. If isShowPast = true: use oldest record
     */
    const calculateInitialDate = (): string => {
      // Extract dates from tableData (filtered data - only visible records)
      const visibleDates = extractVisibleDatesFromTableData();

      // If no visible records, check if selectedPlanId exists for initial page load highlight
      // IMPORTANT: Only use selectedPlanId when there are NO visible records (initial page load)
      // When there are visible records, use logic case 2/3 based on filter
      if (visibleDates.length === 0) {
        return handleNoVisibleRecords();
      }

      // Sort dates
      const sortedDates = [...visibleDates].sort((a, b) => a.localeCompare(b));
      const todayStr = getTodayString();

      // Check if selectedPlanId date matches the expected logic (for highlighting when entering page)
      const selectedPlanIdDate = getSelectedPlanIdDateFromTableData(visibleDates);

      if (filters?.isShowPast) {
        return calculateInitialDateWithIsShowPastTrue(sortedDates);
      } else {
        return calculateInitialDateWithIsShowPastFalse(sortedDates, todayStr, selectedPlanIdDate);
      }
    };

    const finalInitialDate = calculateInitialDate();

    return {
      dateCountMap: countMap,
      eventDates: dates,
      initialDate: finalInitialDate,
      minDate: min,
      maxDate: max,
    };
  }, [
    data,
    tableData,
    fullTableData,
    visibleRowIndices,
    editedRowIndices,
    oldRecordIndices,
    selectedPlanId,
    filters,
  ]);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CalendarIcon sx={{ color: 'primary.main' }} />
        <Typography variant='h6' sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
          予定編集
        </Typography>
      </Box>

      {/* Calendar */}
      <Box>
        <CustomDatePicker
          key={`${placementDatesKey || 'default'}-${initialDate}-${filters?.isShowPast ? 'past' : 'future'}`}
          value={selectedDate}
          onChange={handleDateChange}
          eventDates={eventDates}
          eventCountMap={dateCountMap}
          initialDate={initialDate}
          minDate={minDate}
          maxDate={maxDate}
          disableDatesByRange={false}
          inline
          showTodayButton={false}
          showClearButton={false}
          hideOtherMonthDates={true}
          disabled={true}
        />
      </Box>
    </Paper>
  );
};

export default PlanCalendarSection;
